import React from 'react';
import { X } from 'lucide-react';
import { ScheduledMeal } from '../types';

interface MealDetailsModalProps {
  meal: ScheduledMeal;
  onClose: () => void;
}

const MealDetailsModal: React.FC<MealDetailsModalProps> = ({ meal, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-semibold">{meal.mealTitle}</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <img 
                src={meal.image} 
                alt={meal.mealTitle} 
                className="w-full h-48 object-cover rounded-lg"
              />
              
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">Meal Details</h3>
                <p className="text-gray-700">{meal.description}</p>
                
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium">{meal.type}</div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="text-sm text-gray-500">Quantity</div>
                    <div className="font-medium">{meal.qty}</div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="text-sm text-gray-500">Shift</div>
                    <div className="font-medium capitalize">{meal.shift}</div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="text-sm text-gray-500">Status</div>
                    <div className={`font-medium ${meal.prepared ? 'text-primary-600' : 'text-amber-600'}`}>
                      {meal.prepared ? 'Prepared' : 'Not Prepared'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">Nutritional Information</h3>
              
              <div className="space-y-3">
                <NutrientBar 
                  label="Carbohydrates" 
                  value={meal.carbohydratesPercent} 
                  metric={meal.carbohydrates}
                />
                <NutrientBar 
                  label="Protein" 
                  value={meal.proteinPercent} 
                  metric={meal.protein} 
                />
                <NutrientBar 
                  label="Fat" 
                  value={meal.fatPercent} 
                  metric={meal.fat} 
                />
                <NutrientBar 
                  label="Energy" 
                  value={meal.energyPercent} 
                  metric={meal.energy} 
                />
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">Student Information</h3>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="text-lg font-medium">{meal.kidName}</div>
                  <div className="text-sm text-gray-600 mt-1">ID: {meal.kidId}</div>
                  <div className="text-sm text-gray-600">
                    Birth Date: {new Date(meal.kidBirthDate).toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    Gender: {meal.kidGender.charAt(0).toUpperCase() + meal.kidGender.slice(1)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {meal.note && (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-2">Notes</h3>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-amber-800">{meal.note}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="border-t p-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors mr-2"
          >
            Close
          </button>
          <button
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Mark as Prepared
          </button>
        </div>
      </div>
    </div>
  );
};

interface NutrientBarProps {
  label: string;
  value: number;
  metric: string;
}

const NutrientBar: React.FC<NutrientBarProps> = ({ label, value, metric }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-gray-600">{metric}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-primary-500 h-2.5 rounded-full" 
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

export default MealDetailsModal;